import AWS from "aws-sdk";
import { Event, Ticket } from "JS/Models/Events";
import { config } from "JS/Config";
import { GetObjectRequest } from "aws-sdk/clients/s3";
import { BaseService } from "../BaseService";
import { EventsHelper } from "./EventsHelper";
import { appConstants } from "JS/Helpers/Constants";

export enum TicketError {
    NoEvents,
    NoneRegistered,
}

export interface ImageResponse {
    AcceptRanges: string;
    Body: Uint8Array;
    ContentLength: number;
    ContentType: string;
    ETag: string;
    LastModified: Date;
}

export class TicketsService extends BaseService {
    private helper = new EventsHelper();

    async getEvents(): Promise<Event[]> {
        return new Promise((eventRes, eventRej) => {
            this.helper.initConfig();
            const s3 = this.helper.getS3();
            let validEvents: Event[] = [];
            let eventsWithTickets: Event[] = [];

            s3.listObjectsV2(this.allEventParams(), (err, data) => {
                if (err) {
                    eventRej(TicketError.NoEvents);
                } else {
                    Promise.all(
                        data.Contents.map(
                            (content) =>
                                new Promise<Event>((singleEventRes, _) => {
                                    s3.getObject(
                                        this.getEventFileParams(content.Key),
                                        (err, data) => {
                                            if (err) singleEventRes(null);
                                            const event: Event = JSON.parse(
                                                data.Body.toString(),
                                            );
                                            if (this.shouldShowEvent(event)) {
                                                let folder =
                                                    this.getFolderNameFromKey(
                                                        content.Key,
                                                    );
                                                validEvents.push(event);
                                                Promise.all([
                                                    this.helper.primaryTicketPromise(
                                                        folder,
                                                    ),
                                                    this.helper.secondaryTicketPromise(
                                                        folder,
                                                    ),
                                                ]).then((values) => {
                                                    if (
                                                        values[0] !== null ||
                                                        values[1] !== null
                                                    ) {
                                                        const ticket: Ticket[] =
                                                            [];
                                                        values &&
                                                            values.length > 0 &&
                                                            values.map(
                                                                (item) =>
                                                                    item !==
                                                                        null &&
                                                                    ticket.push(
                                                                        JSON.parse(
                                                                            item?.Body.toString(),
                                                                        ),
                                                                    ),
                                                            );

                                                        eventsWithTickets.push({
                                                            ...event,
                                                            Tickets: ticket,
                                                        });
                                                        singleEventRes(event);
                                                    } else singleEventRes(null);
                                                });
                                            } else singleEventRes(null);
                                        },
                                    );
                                }),
                        ),
                    ).then((_) => {
                        if (
                            eventsWithTickets.length === 0 &&
                            validEvents.length > 0
                        )
                            eventRej(TicketError.NoneRegistered);
                        else eventRes(eventsWithTickets);
                    });
                }
            });
        });
    }

    async getEventByName(eventFolderName: string): Promise<Event[]> {
        return new Promise((eventRes, eventRej) => {
            this.helper.initConfig();
            const s3 = this.helper.getS3();
            let eventsWithTickets: Event[] = [];

            const eventKey = `${this.helper.folder}/tix/${eventFolderName}.json`;

            s3.getObject(this.getEventFileParams(eventKey), (err, data) => {
                if (err || !data?.Body) {
                    eventRej(TicketError.NoEvents);
                    return;
                }

                try {
                    const event: Event = JSON.parse(data.Body.toString());
                    const folder = this.getFolderNameFromKey(eventKey);
                    Promise.all([
                        this.helper.primaryTicketPromise(folder),
                        this.helper.secondaryTicketPromise(folder),
                    ])
                        .then((values) => {
                            const tickets: Ticket[] = [];

                            values.forEach((item) => {
                                if (item && item.Body) {
                                    tickets.push(
                                        JSON.parse(item.Body.toString()),
                                    );
                                }
                            });

                            if (tickets.length > 0) {
                                eventsWithTickets.push({
                                    ...event,
                                    Tickets: tickets,
                                });
                                eventRes(eventsWithTickets);
                            } else {
                                eventRej("No tickets found");
                            }
                        })
                        .catch(() => eventRej(TicketError.NoEvents));
                } catch {
                    eventRej(TicketError.NoEvents);
                }
            });
        });
    }

    getS3PostFileUrl = (fileName: string, isPrimaryUser: number) =>
        this.ngCall<string>(
            "get",
            appConstants.ngEndpoints.pictureUrl(config.user.memberId),
            {
                headers: {
                    Authorization: `Bearer ${config?.ngAccesstoken}`,
                },
                params: {
                    isPrimaryUser,
                },
            },
        );

    async setProfilePhotoInS3(
        file: File | Blob,
        url: string,
    ): Promise<Response> {
        const response = await fetch(url, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": file.type,
            },
        });

        if (!response.ok) {
            throw new Error(`S3 upload failed: ${response.statusText}`);
        }

        return response;
    }
    getBarCode(userId: string, isPrimaryUser: number) {
        return this.ngCall<string>(
            "get",
            appConstants.ngEndpoints.getBarCode(userId),
            {
                headers: {
                    Authorization: `Bearer ${config?.ngAccesstoken}`,
                },
                params: {
                    isPrimaryUser,
                },
            },
        );
    }

    async getPdfBlob(url: string): Promise<Blob> {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }
        return await response.blob();
    }

    async getProfImageByS3(userId: string) {
        return new Promise((reslove, reject) => {
            this.helper.initConfig();
            const s3 = this.helper.getS3();
            let key = `${this.helper.imagePath}${userId}.jpg`;
            s3.getObject(
                this.getEventFileParams(key),
                (err, res: ImageResponse) => {
                    if (err) {
                        reject(false);
                    } else {
                        if (res?.ContentLength > 0) {
                            reslove(true);
                        } else {
                            reslove(false);
                        }
                    }
                },
            );
        });
    }

    private allEventParams(): AWS.S3.ListObjectsV2Request {
        return {
            Bucket: this.helper.bucket,
            Prefix: `${this.helper.folder}/tix/`,
            Delimiter: "/",
        };
    }

    private getFolderNameFromKey(key: string): string {
        if (key.includes(".")) return key.substring(0, key.lastIndexOf("."));
        else return key;
    }

    private getEventFileParams(key: string): GetObjectRequest {
        return {
            Bucket: config?.s3?.events?.bucketName,
            Key: key,
            ResponseCacheControl: "no-cache",
        };
    }

    private shouldShowEvent(event: Event): boolean {
        const eventDate = new Date(event.EventEndDateAndTime);
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        return Date.now() - eventDate.getTime() < sevenDays;
    }
}
